import React from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Box, Link, Typography } from "@mui/material";
import HowToLink from "./HowToLink";
import CopyValueButton from "./CopyValueButton";

// Matches inline how-to guide links: [[howto:guide-id|visible link text]].
// Written directly into lesson/task markdown so a click path (e.g. "Insert ->
// Clustered Bar Chart") opens the matching step-by-step guide from
// data/howToGuides.js instead of leaving learners to guess the menu path.
const HOWTO_LINK_RE = /^\[\[howto:([a-z0-9-]+)\|([^\]]+)\]\]$/;

const AUTO_HOWTO_GUIDES = [
  {
    guideId: 'github-pat-settings',
    test: (text) => /github pat|personal access token|paste the token|developer settings/i.test(text),
  },
  {
    guideId: 'github-clone-repo',
    test: (text) => /clone .*repo|git clone|clone .*vs code|open the cloned repo/i.test(text),
  },
  {
    guideId: 'github-branch-pr',
    test: (text) => /pull request|compare & pull request|git checkout -b|feature branch|branch.*pr|open .*pr|merge pull request/i.test(text),
  },
  {
    guideId: 'github-commit-push',
    test: (text) => /git add|git commit|git push|push to github|commit message/i.test(text),
  },
  {
    guideId: 'excel-chart-reference-line',
    test: (text) => /reference line|target line|scatter series overlay/i.test(text),
  },
  {
    guideId: 'excel-worksheet-gridlines',
    test: (text) => /gridlines/i.test(text) && /dashboard|worksheet|sheet|view tab|white background/i.test(text),
  },
  {
    guideId: 'excel-chart-data-labels-gridlines',
    test: (text) => /actual bars.*data labels|data labels.*actual bars|chart elements|remove gridlines and chart border|remove gridlines, add data labels/i.test(text),
  },
  {
    guideId: 'excel-chart-title',
    test: (text) => /click .*chart title|chart title text|default "Chart Title"|excel.*chart title/i.test(text),
  },
  {
    guideId: 'excel-format-data-point',
    test: (text) => /format data point|change .+ colour|change .+ color/i.test(text),
  },
  {
    guideId: 'excel-insert-chart',
    test: (text) => /insert\s*(→|->|-)?\s*clustered (bar|column) chart|clustered (bar|column) chart/i.test(text),
  },
  {
    guideId: 'excel-pivot-report-connections',
    test: (text) => /report connections|pivottable connections|connect slicers? to|slicers? .*all pivot tables/i.test(text),
  },
  {
    guideId: 'excel-pivot-slicer',
    test: (text) => /insert slicer|add a slicer|pivot.*slicer|slicer.*pivot/i.test(text),
  },
  {
    guideId: 'excel-pivot-refresh',
    test: (text) => /refresh.*pivot|pivot.*refresh|new rows.*pivot|source range/i.test(text),
  },
  {
    guideId: 'excel-pivot-table',
    test: (text) => /insert\s*(→|->|-)?\s*pivottable|pivot\s*table|pivottable/i.test(text),
  },
  {
    guideId: 'excel-xlookup',
    test: (text) => /\bxlookup\b/i.test(text),
  },
  {
    guideId: 'excel-vlookup',
    test: (text) => /\bvlookup\b/i.test(text),
  },
  {
    guideId: 'excel-index-match',
    test: (text) => /index\s*\/\s*match|index\s+match/i.test(text),
  },
  {
    guideId: 'excel-conditional-formatting',
    test: (text) => /conditional formatting|highlight cells rules/i.test(text),
  },
  {
    guideId: 'excel-sort-table',
    test: (text) => /sort largest|sort smallest|sort .*table|sort .*range|sort .*column|sort .*rows|sort .*transactions|sort .*amount|ascending.*descending|descending.*ascending/i.test(text),
  },
  {
    guideId: 'excel-filter-table',
    test: (text) => /autofilter|clear filter|visible rows|filter .*status|filter .*table|filter .*range|filter .*rows|filter .*values|filter .*pending|filter .*returned/i.test(text),
  },
  {
    guideId: 'tableau-calculated-field',
    test: (text) => /tableau.*calculated field|calculated field.*tableau|create calculated field/i.test(text),
  },
  {
    guideId: 'tableau-shelf-basics',
    test: (text) => /rows shelf|columns shelf|marks card|show me panel/i.test(text),
  },
  {
    guideId: 'python-create-venv',
    test: (text) => /python -m venv|virtual environment|activate .*venv|\bvenv\b/i.test(text),
  },
  {
    guideId: 'python-pip-install',
    test: (text) => /pip install|requirements\.txt|pip freeze/i.test(text),
  },
  {
    guideId: 'python-run-pytest',
    test: (text) => /pytest|run tests|test file|unit test/i.test(text),
  },
  {
    guideId: 'python-jupyter-notebook',
    test: (text) => /jupyter|\.ipynb|notebook kernel|open .*notebook|run .*cell|select .*kernel/i.test(text),
  },
  {
    guideId: 'pandas-read-csv',
    test: (text) => /read_csv|load .*csv|csv .*pandas|df\.head|df\.info/i.test(text),
  },
  {
    guideId: 'pandas-merge',
    test: (text) => /pandas.*merge|merge\(|join .*dataframe|dataframe.*join|left\.merge/i.test(text),
  },
  {
    guideId: 'pandas-groupby',
    test: (text) => /groupby|group by .*pandas|\.agg\(|summari[sz]e .*dataframe|dataframe.*group/i.test(text),
  },
  {
    guideId: 'python-matplotlib-chart',
    test: (text) => /matplotlib|plt\.|seaborn|plot .*chart|savefig|plt\.show/i.test(text),
  },
  {
    guideId: 'azure-create-resource-group',
    test: (text) => /azure resource group|resource group.*azure|rg-[a-z0-9-]+|create .*azure resource/i.test(text),
  },
  {
    guideId: 'azure-storage-container',
    test: (text) => /storage account|blob container|adls gen2|upload .*dataset|upload .*file.*container/i.test(text),
  },
  {
    guideId: 'azure-adf-pipeline',
    test: (text) => /data factory|adf|copy data|pipeline.*debug|publish .*pipeline/i.test(text),
  },
  {
    guideId: 'azure-key-vault-secret',
    test: (text) => /key vault|managed identity.*secret|secret.*key vault/i.test(text),
  },
  {
    guideId: 'databricks-notebook-cluster',
    test: (text) => /databricks|attach .*cluster|cluster.*notebook|delta lake/i.test(text),
  },
  {
    guideId: 'fabric-notebook-lakehouse',
    test: (text) => /fabric.*notebook|lakehouse|attach .*lakehouse|notebook.*lakehouse/i.test(text),
  },
];

const autoHowToGuideForText = (text = '') => {
  const raw = String(text || '');
  if (raw.includes('[[howto:')) return null;
  return AUTO_HOWTO_GUIDES.find((guide) => guide.test(raw))?.guideId || null;
};

const COLORS = {
  prussianBlue: {
    secondary: "#034975",
  },
  gold: {
    primary: "#CD9575",
    secondary: "#D8A791",
    tertiary: "#E3B9A7",
  },
  darkSlate: {
    tertiary: "#456666",
  },
};

// Callout label → colour config
const CALLOUT_CONFIGS = {
  WHY:      { color: '#4dabf7', bg: 'rgba(77,171,247,0.08)',   border: 'rgba(77,171,247,0.35)'  },
  WHEN:     { color: '#ff922b', bg: 'rgba(255,146,43,0.08)',   border: 'rgba(255,146,43,0.35)'  },
  HOW:      { color: '#69db7c', bg: 'rgba(105,219,124,0.08)',  border: 'rgba(105,219,124,0.35)' },
  WHERE:    { color: '#da77f2', bg: 'rgba(218,119,242,0.08)',  border: 'rgba(218,119,242,0.35)' },
  SCENARIO: { color: '#20c997', bg: 'rgba(32,201,151,0.08)',   border: 'rgba(32,201,151,0.35)' },
  SECTOR:   { color: '#94d82d', bg: 'rgba(148,216,45,0.07)',   border: 'rgba(148,216,45,0.3)'  },
};

const flushParagraph = (blocks, paragraph) => {
  if (!paragraph.length) return;
  const text = paragraph.join(" ");

  // Detect callout pattern: **WHY:** / **WHEN:** / **HOW:** / **WHERE:** / **SCENARIO:** / **SECTOR:** / **WHY something?**
  const m = /^\*\*(WHY|WHEN|HOW|WHERE|SCENARIO|SECTOR)([^*]*)?\*\*[:\s]*/i.exec(text);
  if (m) {
    const key        = m[1].toUpperCase();
    // Strip leading/trailing colons and whitespace — e.g. ": E-commerce" → "E-commerce"
    const rawRest    = (m[2]?.trim() || '').replace(/^[:\s]+/, '').replace(/[:\s]*$/, '').trim();
    const bodyFromText = text.slice(m[0].length).trim();
    // Merge rawRest into body text (badge label is always just the keyword)
    const body = rawRest
      ? (bodyFromText ? `${rawRest} — ${bodyFromText}` : rawRest)
      : bodyFromText;
    blocks.push({ type: 'callout', key, label: key, body });
  } else {
    blocks.push({ type: "paragraph", text });
  }
  paragraph.length = 0;
};

const flushList = (blocks, list) => {
  if (!list.items.length) return;
  blocks.push({ type: list.ordered ? "ordered-list" : "list", items: [...list.items] });
  list.items.length = 0;
  list.ordered = false;
};

const parseTableRow = (line) =>
  line.split('|').slice(1, -1).map((c) => c.trim());

const isSeparatorRow = (line) => /^\|[\s|:-]+\|$/.test(line.trim());

const parseMarkdown = (content = "") => {
  const blocks = [];
  const paragraph = [];
  const list = { ordered: false, items: [] };
  const lines = String(content || "").replace(/\r\n/g, "\n").split("\n");

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmed = line.trim();

    if (trimmed.startsWith("```")) {
      flushParagraph(blocks, paragraph);
      flushList(blocks, list);
      const language = trimmed.slice(3).trim() || "text";
      const code = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        code.push(lines[index]);
        index += 1;
      }
      blocks.push({ type: "code", language, code: code.join("\n") });
      continue;
    }

    if (!trimmed) {
      flushParagraph(blocks, paragraph);
      flushList(blocks, list);
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (heading) {
      flushParagraph(blocks, paragraph);
      flushList(blocks, list);
      blocks.push({ type: `h${heading[1].length}`, text: heading[2] });
      continue;
    }

    const unordered = /^[-*]\s+(.+)$/.exec(trimmed);
    const ordered = /^\d+\.\s+(.+)$/.exec(trimmed);
    if (unordered || ordered) {
      flushParagraph(blocks, paragraph);
      const isOrdered = Boolean(ordered);
      if (list.items.length && list.ordered !== isOrdered) {
        flushList(blocks, list);
      }
      list.ordered = isOrdered;
      list.items.push((unordered || ordered)[1]);
      continue;
    }

    // Markdown table: line starts and ends with |
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushParagraph(blocks, paragraph);
      flushList(blocks, list);
      // Collect the whole table
      const rows = [];
      while (index < lines.length) {
        const tl = lines[index].trim();
        if (!tl.startsWith('|') || !tl.endsWith('|')) break;
        if (!isSeparatorRow(tl)) rows.push(parseTableRow(tl));
        index += 1;
      }
      index -= 1; // outer loop will increment
      if (rows.length >= 2) {
        blocks.push({ type: 'table', headers: rows[0], rows: rows.slice(1) });
      } else if (rows.length === 1) {
        blocks.push({ type: 'table', headers: rows[0], rows: [] });
      }
      continue;
    }

    flushList(blocks, list);
    paragraph.push(trimmed);
  }

  flushParagraph(blocks, paragraph);
  flushList(blocks, list);
  return blocks;
};

// Renders inline text with **bold** and `code` support
const InlineText = ({ text, onOpenMarkdownLink }) => (
  <>
    {String(text)
      .split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((?:https?:\/\/|\/)[^)]+\)|\[\[howto:[a-z0-9-]+\|[^\]]+\]\])/g)
      .map((part, index) => {
        const howToMatch = HOWTO_LINK_RE.exec(part);
        if (howToMatch) {
          return (
            <HowToLink key={index} guideId={howToMatch[1]}>
              {howToMatch[2]}
            </HowToLink>
          );
        }
        const markdownLink = /^\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)]+)\)$/.exec(part);
        if (markdownLink) {
          const isExternal = /^https?:\/\//.test(markdownLink[2]);
          const isInternalMarkdown = markdownLink[2].startsWith('/') && markdownLink[2].split('#')[0].endsWith('.md');
          return (
            <Link
              key={index}
              href={markdownLink[2]}
              onClick={isInternalMarkdown && onOpenMarkdownLink
                ? (event) => {
                    event.preventDefault();
                    onOpenMarkdownLink({ href: markdownLink[2], label: markdownLink[1] });
                  }
                : undefined}
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noreferrer" : undefined}
              sx={{ overflowWrap: 'anywhere' }}
            >
              {markdownLink[1]}
            </Link>
          );
        }
        if (part.startsWith("`") && part.endsWith("`")) {
          return (
            <Box
              key={index}
              component="code"
              sx={{
                backgroundColor: COLORS.darkSlate.tertiary,
                color: COLORS.gold.primary,
                px: 0.5,
                py: 0.15,
                borderRadius: 0.5,
                fontSize: "0.9em",
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {part.slice(1, -1)}
            </Box>
          );
        }
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <Box key={index} component="strong" sx={{ fontWeight: 700 }}>
              {part.slice(2, -2)}
            </Box>
          );
        }
        return part;
      })}
  </>
);

const MarkdownRenderer = ({ content, onOpenMarkdownLink }) => {
  const blocks = parseMarkdown(content);

  return (
    <Box
      sx={{
        maxWidth: '100%',
        overflowX: 'hidden',
        '& *': {
          minWidth: 0,
          boxSizing: 'border-box',
        },
      }}
    >
      {blocks.map((block, index) => {
        if (block.type === "code") {
          return (
            <Box key={index} sx={{ my: 2, position: 'relative', overflowX: 'auto', maxWidth: '100%', borderRadius: 1 }}>
              <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1, bgcolor: 'rgba(3, 73, 117, 0.9)', borderRadius: 1 }}>
                <CopyValueButton value={block.code} label="block" />
              </Box>
              <SyntaxHighlighter
                style={{
                  ...vscDarkPlus,
                  'code[class*="language-"]': {
                    background: COLORS.prussianBlue.secondary,
                    color: "#F5F5F5",
                  },
                  'pre[class*="language-"]': {
                    background: COLORS.prussianBlue.secondary,
                    color: "#F5F5F5",
                    margin: 0,
                  },
                }}
                language={block.language}
                PreTag="div"
                customStyle={{ margin: 0, borderRadius: 4, fontSize: '0.82rem', paddingRight: '3.5rem' }}
              >
                {block.code}
              </SyntaxHighlighter>
            </Box>
          );
        }

        if (block.type === "h1" || block.type === "h2" || block.type === "h3") {
          const variant = block.type === "h1" ? "h4" : block.type === "h2" ? "h5" : "h6";
          const color = block.type === "h1" ? "text.primary" : block.type === "h2" ? "primary.main" : "text.primary";
          return (
            <Typography
              key={index}
              variant={variant}
              gutterBottom
              sx={{
                textAlign: "left",
                color,
                mt: index ? 2 : 0,
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              }}
            >
              <InlineText text={block.text} onOpenMarkdownLink={onOpenMarkdownLink} />
            </Typography>
          );
        }

        if (block.type === "list" || block.type === "ordered-list") {
          return (
            <Box
              key={index}
              component={block.type === "ordered-list" ? "ol" : "ul"}
              sx={{
                pl: { xs: 2.25, sm: 3 },
                pr: 0,
                textAlign: "left",
                color: "text.primary",
                '& li': {
                  pl: 0.25,
                  mb: 0.5,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                },
              }}
            >
              {block.items.map((item, itemIndex) => {
                const autoGuideId = autoHowToGuideForText(item);
                return (
                  <li key={`${item}-${itemIndex}`}>
                    <Typography component="span" variant="body1" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      <InlineText text={item} onOpenMarkdownLink={onOpenMarkdownLink} />
                      {autoGuideId && (
                        <HowToLink guideId={autoGuideId} compact>
                          Help
                        </HowToLink>
                      )}
                    </Typography>
                  </li>
                );
              })}
            </Box>
          );
        }

        if (block.type === "table") {
          return (
            <Box key={index} sx={{ overflowX: 'auto', my: 2 }}>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.84rem',
                  '& th, & td': {
                    px: 1.5, py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'left',
                    verticalAlign: 'top',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  },
                  '& th': {
                    fontWeight: 700,
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                  },
                  '& tr:last-child td': { borderBottom: 'none' },
                }}
              >
                <thead>
                  <tr>
                    {block.headers.map((h, hi) => (
                      <th key={hi}><InlineText text={h} onOpenMarkdownLink={onOpenMarkdownLink} /></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci}><Typography component="span" variant="body2" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}><InlineText text={cell} onOpenMarkdownLink={onOpenMarkdownLink} /></Typography></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          );
        }

        if (block.type === "callout") {
          const cfg = CALLOUT_CONFIGS[block.key] || CALLOUT_CONFIGS.WHY;
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                mb: 1.5,
                p: '10px 14px',
                borderLeft: `4px solid ${cfg.border}`,
                bgcolor: cfg.bg,
                borderRadius: '0 8px 8px 0',
              }}
            >
              {/* Badge shows only the keyword, e.g. WHY / WHEN / HOW / WHERE */}
              <Box
                component="span"
                sx={{
                  minWidth: 50,
                  fontWeight: 800,
                  fontSize: '0.62rem',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  color: cfg.color,
                  lineHeight: '1.6rem',
                  flexShrink: 0,
                }}
              >
                {block.key}
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.65, color: 'text.primary', flex: 1, minWidth: 0 }}>
                {block.body ? <InlineText text={block.body} onOpenMarkdownLink={onOpenMarkdownLink} /> : null}
              </Typography>
            </Box>
          );
        }

        return (
          <Typography
            key={index}
            variant="body1"
            paragraph
            sx={{
              textAlign: "left",
              color: "text.primary",
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
            }}
          >
            <InlineText text={block.text} onOpenMarkdownLink={onOpenMarkdownLink} />
          </Typography>
        );
      })}
    </Box>
  );
};

export default MarkdownRenderer;
